import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import { ChatService, ChatMessage, ChatThread } from '../../../services-http/chat/chat.service';
import { ChatSoundService } from '../../../services-http/chat/chat-sound.service';
import { WebsocketService } from '../../../services-http/websocket/websocket.service';
import Swal from 'sweetalert2';

// Sound toggle icon: bell on / bell off.
const SOUND_ON_LABEL = '🔔';
const SOUND_OFF_LABEL = '🔕';

@Component({
  selector: 'app-chatbox',
  templateUrl: './chatbox.component.html',
  styleUrls: ['./chatbox.component.scss']
})
export class ChatboxComponent implements OnInit, OnDestroy {

  /** Scroll container for the message thread (auto-scroll target). */
  @ViewChild('messageList') private messageList: ElementRef;

  // --- Identity -----------------------------------------------------
  /** Logged-in user record from localStorage (set by AuthService). */
  currentUser: any = {};
  /** My numeric login id. */
  myId = 0;

  // --- Conversation list (left pane) --------------------------------
  threads: ChatThread[] = [];
  loadingThreads = false;
  threadSearch = '';

  // --- Open thread (right pane) -------------------------------------
  activeThread: ChatThread = null;
  messages: ChatMessage[] = [];
  loadingMessages = false;

  // --- Composer ------------------------------------------------------
  draft = '';
  sending = false;

  // --- Realtime status ------------------------------------------------
  connected = false;
  reconnectCount = 0;
  soundOn = true;

  /** unread count per login_id, e.g. { 7: 2 } */
  unreadByUser: { [loginId: number]: number } = {};

  /** Emits on destroy; every subscription takes until here. */
  private readonly destroy$ = new Subject<void>();
  /** Fingerprints of messages already shown (survives reconnect replay). */
  private readonly seenFingerprints: { [key: string]: boolean } = {};

  constructor(
    private chat: ChatService,
    private realtime: WebsocketService,
    private sound: ChatSoundService,
  ) { }




  // --- Lifecycle ------------------------------------------------------

  ngOnInit(): void {
    this.currentUser = this.readCurrentUser();
    this.myId = this.toId(this.currentUser && this.currentUser.iloginId);
    if (this.myId <= 0) {
      console.warn('[CHAT] no valid user id; realtime connect will be skipped', this.myId);
    }

    this.loadThreads();

    // One shared socket for the whole app; safe to call repeatedly.
    this.realtime.connect({ userId: this.myId });

    // Connection pill.
    if (this.realtime && this.realtime.connected$) {
      this.realtime.connected$
        .pipe(takeUntil(this.destroy$))
        .subscribe((open) => {
          this.connected = !!open;
          if (open) { this.reconnectCount = 0; }
        });
    }

    // Incoming chat frames.
    if (this.realtime && this.realtime.chat$) {
      this.realtime.chat$
        .pipe(takeUntil(this.destroy$))
        .subscribe((frame) => this.onRealtimeFrame(frame));
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** localStorage record saved at login; never throws when missing. */
  private readCurrentUser(): any {
    try {
      return JSON.parse(localStorage.getItem('currentUser')) || {};
    } catch (e) {
      return {};
    }
  }


  // --- Threads ----------------------------------------------------------

  /** Left pane: everyone I can chat with, ordered by newest message first. */
  loadThreads(): void {
    this.loadingThreads = true;
    this.chat.getThreads()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loadingThreads = false),
      )
      .subscribe(
        (threads) => {
          this.threads = (threads || []).slice();
          // Seed unread counts from server thread data so badges show immediately.
          for (const t of this.threads) {
            const c = this.countUnread(t.message);
            if (c > 0) { this.unreadByUser[this.toId(t.login_id)] = c; }
          }
          this.sortThreads();
        },
        (error) => Swal.fire('Error', this.readError(error) || 'Failed to load conversations.', 'error'),
      );
  }

  /** Milliseconds-since-epoch of a thread's newest message, or 0 if none. */
  private threadSortKey(thread: ChatThread): number {
    const msgs = thread && thread.message;
    if (!msgs || !msgs.length) { return 0; }
    const last = msgs[0];
    if (!last) { return 0; }
    const ts = last.created_at ? new Date(last.created_at).getTime() : 0;
    return isNaN(ts) ? 0 : ts;
  }

  /** Sort threads in place by newest message first (threads with no messages sink to the bottom). */
  private sortThreads(): void {
    this.threads.sort((a, b) => this.threadSortKey(b) - this.threadSortKey(a));
  }

  /** Click a row: highlight it, clear its badge, fetch history. */
  openThread(thread: ChatThread): void {
    this.activeThread = thread;
    if (thread) {
      this.unreadByUser[thread.login_id] = 0;
      if (thread.message && thread.message.length) {
        thread.message[0].read = 1;
      }
      this.loadMessages(thread.login_id);
    }
  }

  isActive(thread: ChatThread): boolean {
    return !!thread && !!this.activeThread && thread.login_id === this.activeThread.login_id;
  }

  trackThread(_index: number, thread: ChatThread): number {
    return thread.login_id;
  }

  trackMessage(_index: number, msg: ChatMessage): string {
    if (msg && msg.id !== undefined && msg.id !== null) { return 'id:' + msg.id; }
    return [msg.sender, msg.receiver, msg.message, msg.created_at].join('|');
  }

  /** Count messages with read == 0 in a message array. */
  private countUnread(messages: ChatMessage[]): number {
    if (!messages || !messages.length) { return 0; }
    return messages.filter((m: ChatMessage) => m.read === 0).length;
  }

  unreadCount(loginId: number): number {
    return this.unreadByUser[loginId] || 0;
  }

  // --- Messages ---------------------------------------------------------

  /** Right pane: history with one person. */
  loadMessages(loginId: number): void {
    this.messages = [];
    this.loadingMessages = true;
    this.chat.getMessages(loginId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.loadingMessages = false),
      )
      .subscribe(
        (raw) => {
          const messages = this.normalizeMessages(raw);
          this.messages = messages;
          this.rememberAll(this.messages);
          // Restore unread count from loaded history (openThread reset it to 0).
          this.unreadByUser[loginId] = this.countUnread(messages);
          this.scrollToBottom();
        },
        (error) => Swal.fire('Error', this.readError(error) || 'Failed to load messages.', 'error'),
      );
  }

  /** Coerce the HTTP response into a ChatMessage[] (handles wrapped payloads). */
  private normalizeMessages(raw: any): ChatMessage[] {
    if (Array.isArray(raw)) { return raw; }
    if (raw && Array.isArray(raw.messages)) { return raw.messages; }
    if (raw && Array.isArray(raw.data)) { return raw.data; }
    return [];
  }

  /** Send the draft through the shared socket, then refresh from server. */
  sendMessage(): void {
    const text = (this.draft || '').trim();
    const to = this.activeThread && this.activeThread.login_id;
    if (!text || !to || this.sending) { return; }

    // Clear first so double-clicks / Enter repeats can't resend.
    this.draft = '';
    this.sending = true;

    const sent = this.realtime.sendChat(this.myId, to, text);
    if (!sent) {
      this.sending = false;
      Swal.fire('Offline', 'Realtime connection is not open. Message was not sent.', 'warning');
      return;
    }
    // The server persists, then we re-read the thread so the new bubble
    // comes back exactly once with its real id/timestamp.
    this.chat.getMessages(to)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.sending = false),
      )
      .subscribe(
        (messages) => {
          this.messages = this.normalizeMessages(messages);
          this.rememberAll(this.messages);
          this.scrollToBottom();
        },
        () => Swal.fire('Error', 'Message sent, but the thread failed to refresh.', 'error'),
      );
  }


  // --- Realtime -----------------------------------------------------------

  /**
   * One incoming socket frame. Chat frames look like
   * { type: 'message', api: 'chat', sender, receiver, message, ... }.
   * Anything without sender/receiver/message is server housekeeping.
   */
  private onRealtimeFrame(frame: any): void {
    if (!frame || frame.sender === undefined || frame.receiver === undefined
        || typeof frame.message === 'undefined') {
      console.warn('[CHAT] dropped malformed frame:', frame);
      return;
    }
    const from = this.toId(frame.sender);
    const to = this.toId(frame.receiver);
    const openId = this.activeThread && this.toId(this.activeThread.login_id);
    const mine = this.myId;

    if (openId && ((from === openId && to === mine) || (from === mine && to === openId))) {
      if (!this.alreadySeen(frame)) {
        this.markSeen(frame);
        this.messages.push(frame);
        this.scrollToBottom();
      }
      // New message for the open thread: it's unread until explicitly marked read.
      this.unreadByUser[openId] = (this.unreadByUser[openId] || 0) + 1;
      this.playSound();
      return;
    }

    // Another conversation: badge it and refresh its preview line.
    const other = (from === mine) ? to : from;
    if (other) {
      this.unreadByUser[other] = (this.unreadByUser[other] || 0) + 1;
      this.refreshPreview(other, frame.message);
    }
    this.playSound();
  }

  private refreshPreview(loginId: number, text: string): void {
    for (const thread of this.threads) {
      if (this.toId(thread.login_id) === loginId) {
        thread.message = [{
          sender: loginId,
          receiver: this.myId,
          message: text,
          read: 0,
          created_at: new Date().toISOString(),
        } as ChatMessage];
        this.sortThreads();
        return;
      }
    }
  }

  // --- View helpers ---------------------------------------------------------

  get connectionLabel(): string {
    if (this.connected) { return 'Online'; }
    return 'Offline';
  }

  get soundLabel(): string {
    return this.soundOn ? SOUND_ON_LABEL : SOUND_OFF_LABEL;
  }

  toggleSound(): void {
    this.soundOn = !this.soundOn;
  }

  isMine(msg: ChatMessage): boolean {
    return !!msg && this.toId(msg.sender) === this.myId;
  }

  senderName(msg: ChatMessage): string {
    if (this.isMine(msg)) { return 'YOU'; }
    return ((this.activeThread && this.activeThread.pkid_name) || 'THEM').toUpperCase();
  }

  playSound(): void {
    if (this.soundOn) { this.sound.play(); }
  }

  scrollToBottom(): void {
    setTimeout(() => {
      try {
        const el = this.messageList && this.messageList.nativeElement;
        if (el) { el.scrollTop = el.scrollHeight; }
      } catch (e) { /* container not rendered yet */ }
    }, 50);
  }

  // --- Small utilities -------------------------------------------------------

  /** '42' and 42 are the same user; unparseable ids become 0. */
  private toId(value: any): number {
    const id = parseInt(value, 10);
    return isNaN(id) ? 0 : id;
  }

  private fingerprint(msg: ChatMessage): string {
    if (msg && msg.id !== undefined && msg.id !== null) { return 'id:' + msg.id; }
    return [msg.sender, msg.receiver, msg.message, msg.created_at].join('|');
  }

  private alreadySeen(msg: ChatMessage): boolean {
    return !!this.seenFingerprints[this.fingerprint(msg)];
  }

  private markSeen(msg: ChatMessage): void {
    this.seenFingerprints[this.fingerprint(msg)] = true;
  }

  private rememberAll(messages: ChatMessage[]): void {
    for (const msg of messages) { this.markSeen(msg); }
  }

  /** Turns a { field: reason } error body into 'reason<br/>…' text. */
  private readError(error: any): string {
    const body = error && error.error ? error.error : {};
    let text = '';
    Object.keys(body).forEach((key) => { text += body[key] + '<br/>'; });
    return text;
  }

}
